import React from 'react';
import { Box, Button } from '@apia/theme';
import uniqueId from 'lodash-es/uniqueId';
import isFunction from 'lodash-es/isFunction';
import {
  customEvents,
  persistentStorage,
  enableChildrenFocus,
  disableChildrenFocus as disableChildrenFocusMethod,
  useUpdateEffect,
} from '@apia/util';
import { getVariant, injectStyles } from '@apia/theme';
import AnimateHeight from 'react-animate-height';
import collapsiblePanel from './collapsiblePanel';
import { ICollapsiblePanel } from './types';
import { Icon } from '@apia/icons';

injectStyles('layout.common.components.collapsiblePanel', collapsiblePanel);

export const CollapsiblePanel = React.forwardRef<
  HTMLDivElement,
  ICollapsiblePanel
>(
  (
    {
      children,
      className,
      closedIcon = 'ArrowDownThin',
      collapsed,
      defaultCollapsed,
      disableChildrenFocus,
      iconSize = '20px',
      id,
      onClose,
      onOpen,
      openIcon = 'ArrowRightThin',
      label,
      rememberCollapsedState,
      titleButtons,
      minHeight = 0,
      'aria-hidden': ariaHidden,
      collapseIconToRight,
    },
    outerRef,
  ) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const prefix = React.useMemo(() => (id ? '' : uniqueId()), []);
    const actualId = `${prefix}_${id ?? 'collapsiblePanel'}`;

    const [isCollapsed, setIsCollapsed] = React.useState<boolean>(
      rememberCollapsedState
        ? ((persistentStorage[`collapsiblePanel${actualId}`] ??
            defaultCollapsed ??
            collapsed) as boolean)
        : (defaultCollapsed ??
            collapsed ??
            (persistentStorage[`collapsiblePanel${actualId}`] as boolean)),
    );

    const [ref, setRef] = React.useState<HTMLDivElement | null>(null);

    const callOnOpen = React.useCallback(
      (updateState: unknown, ev?: React.MouseEvent) => {
        const onOpenResult = onOpen ? onOpen(ev) : null;
        if (onOpenResult) {
          void onOpenResult.then((result) => {
            if (result) if (updateState) setIsCollapsed((current) => !current);
          });
        } else if (updateState) setIsCollapsed((current) => !current);
      },
      [onOpen],
    );

    useUpdateEffect(() => {
      if (collapsed !== undefined) setIsCollapsed(collapsed);
      if (collapsed === false) callOnOpen(false);
    }, [collapsed]);

    useUpdateEffect(() => {
      if (isCollapsed) {
        if (onClose) onClose();
        persistentStorage[`collapsiblePanel${actualId}`] = true;
        if (ref && disableChildrenFocus)
          disableChildrenFocusMethod(
            ref.querySelector('.collapsiblePanel__content') as HTMLElement,
          );
      } else {
        persistentStorage[`collapsiblePanel${actualId}`] = false;
        if (ref && disableChildrenFocus)
          enableChildrenFocus(
            ref.querySelector('.collapsiblePanel__content') as HTMLElement,
          );
      }
    }, [isCollapsed]);

    React.useEffect(() => {
      const handleCustomFocus = () => setIsCollapsed(false);
      ref
        ?.querySelector('.collapsiblePanel__content')
        ?.addEventListener(customEvents.focus, handleCustomFocus);
      return () => {
        ref
          ?.querySelector('.collapsiblePanel__content')
          ?.removeEventListener(customEvents.focus, handleCustomFocus);
      };
    }, [id, isCollapsed, onClose, onOpen, ref]);

    return (
      <Box
        className={`collapsiblePanel ${className ?? ''}`}
        {...getVariant('layout.common.components.collapsiblePanel')}
        ref={(el: HTMLDivElement) => {
          if (isFunction(outerRef)) outerRef(el);
          setRef(el);
        }}
      >
        <Box className="collapsiblePanel__label">
          <Button
            {...getVariant('inherit')}
            aria-expanded={!isCollapsed}
            onClick={(ev) => callOnOpen(true, ev)}
            type="button"
            className="collapsiblePanel__label__button"
          >
            {!collapseIconToRight && (
              <>
                {isCollapsed ? (
                  <Icon title="" icon={openIcon} size={iconSize} />
                ) : (
                  <Icon title="" icon={closedIcon} size={iconSize} />
                )}
              </>
            )}
            <span className="title">{label}</span>
            {collapseIconToRight && (
              <>
                {isCollapsed ? (
                  <Icon title="" icon={openIcon} size={iconSize} />
                ) : (
                  <Icon title="" icon={closedIcon} size={iconSize} />
                )}
              </>
            )}
          </Button>
          {titleButtons}
        </Box>
        <AnimateHeight
          id={actualId}
          duration={150}
          height={isCollapsed ? minHeight : 'auto'}
          sx={{ '&>div': { display: 'block ' } }}
          aria-hidden={ariaHidden ?? isCollapsed}
          className="collapsiblePanel__content"
        >
          {children}
        </AnimateHeight>
      </Box>
    );
  },
);

CollapsiblePanel.displayName = 'CollapsiblePanel';
